import React, { useEffect, useMemo, useState } from 'react';
import { doc, getDoc, setDoc, serverTimestamp } from 'firebase/firestore';
import { db, auth } from '../firebase';
import { Card, CardContent, CardHeader, CardTitle } from './ui/card';
import { Badge } from './ui/badge';
import toast from 'react-hot-toast';
import { useAuthState } from 'react-firebase-hooks/auth';
import { useSchoolCapabilities, SCHOOL_ACTIONS } from '../lib/auth/capabilities';
import {
  TERM_ORDER,
  TERM_LABELS,
  getAcademicYearLabel,
  resolveKeyStage,
  frameworkForKeyStage,
  getSubjectsForKeyStage,
  getDescriptorOptions,
  normalizeSubjectAttainment,
  emptySubjectAttainment,
  isLegacyRecord,
  KEY_STAGE_LABELS,
  FRAMEWORK_LABELS,
} from '../lib/curriculumAssessment';

export default function CurriculumAttainmentPanel({ studentId, yearGroup, phase }) {
  const [user] = useAuthState(auth);
  const { can } = useSchoolCapabilities();
  const yearLabel = useMemo(() => getAcademicYearLabel(), []);
  const keyStage = useMemo(() => resolveKeyStage(yearGroup, phase), [yearGroup, phase]);
  const framework = useMemo(() => frameworkForKeyStage(keyStage), [keyStage]);
  const subjects = useMemo(() => getSubjectsForKeyStage(keyStage), [keyStage]);
  const descriptorOptions = useMemo(() => getDescriptorOptions(framework), [framework]);
  const canEdit = can(SCHOOL_ACTIONS.RECORD_CURRICULUM_ATTAINMENT);

  const [loading, setLoading] = useState(true);
  const [saving, setSaving] = useState(false);
  const [legacyBanner, setLegacyBanner] = useState(false);
  const [subjectAttainment, setSubjectAttainment] = useState(() => emptySubjectAttainment(subjects));
  const [subjectNotes, setSubjectNotes] = useState(() => emptySubjectAttainment(subjects));

  useEffect(() => {
    const load = async () => {
      if (!studentId) return;
      setLoading(true);
      try {
        const ref = doc(db, 'students', studentId, 'attainmentRecords', yearLabel);
        const snap = await getDoc(ref);
        if (snap.exists()) {
          const data = snap.data();
          setLegacyBanner(isLegacyRecord(data));
          setSubjectAttainment(normalizeSubjectAttainment(data, keyStage));
          const notesBase = emptySubjectAttainment(subjects);
          if (data.subjectNotes) {
            for (const term of TERM_ORDER) {
              const termNotes = data.subjectNotes[term] || {};
              for (const subject of subjects) {
                notesBase[term][subject] = termNotes[subject] ? String(termNotes[subject]) : '';
              }
            }
          }
          setSubjectNotes(notesBase);
        } else {
          setLegacyBanner(false);
          setSubjectAttainment(emptySubjectAttainment(subjects));
          setSubjectNotes(emptySubjectAttainment(subjects));
        }
      } catch (e) {
        console.error('Failed to load attainment', e);
        toast.error('Could not load curriculum attainment');
      } finally {
        setLoading(false);
      }
    };
    load();
  }, [studentId, yearLabel, keyStage, subjects]);

  const updateLevel = (term, subject, value) => {
    setSubjectAttainment((prev) => ({
      ...prev,
      [term]: { ...prev[term], [subject]: value },
    }));
  };

  const updateNote = (term, subject, value) => {
    setSubjectNotes((prev) => ({
      ...prev,
      [term]: { ...prev[term], [subject]: value },
    }));
  };

  const sanitizeTermMap = (termMap) => {
    const out = {};
    for (const subject of subjects) {
      const val = termMap?.[subject];
      out[subject] = val && String(val).trim() ? String(val).trim() : null;
    }
    return out;
  };

  const handleSave = async () => {
    if (!studentId || !user) return;
    setSaving(true);
    try {
      const ref = doc(db, 'students', studentId, 'attainmentRecords', yearLabel);
      const subjectAttainmentPayload = {};
      const subjectNotesPayload = {};
      for (const term of TERM_ORDER) {
        subjectAttainmentPayload[term] = sanitizeTermMap(subjectAttainment[term]);
        subjectNotesPayload[term] = Object.fromEntries(
          subjects.map((s) => [s, subjectNotes[term]?.[s]?.trim() || null]),
        );
      }

      await setDoc(
        ref,
        {
          keyStage,
          assessmentFramework: framework,
          academicYear: yearLabel,
          subjectAttainment: subjectAttainmentPayload,
          subjectNotes: subjectNotesPayload,
          updatedAt: serverTimestamp(),
          updatedBy: user.uid,
          updatedByEmail: user.email || null,
        },
        { merge: true },
      );
      setLegacyBanner(false);
      toast.success('Curriculum attainment saved');
    } catch (e) {
      console.error('Failed to save attainment', e);
      toast.error('Could not save curriculum attainment');
    } finally {
      setSaving(false);
    }
  };

  const panelTitle =
    framework === 'secondary_gcse'
      ? `GCSE subject grades: ${KEY_STAGE_LABELS[keyStage]}`
      : `Curriculum attainment: ${KEY_STAGE_LABELS[keyStage]}`;

  if (loading) {
    return (
      <Card>
        <CardContent className="p-6 text-sm text-gray-500">Loading curriculum attainment…</CardContent>
      </Card>
    );
  }

  return (
    <Card>
      <CardHeader>
        <div className="flex flex-wrap items-start justify-between gap-3">
          <div>
            <CardTitle>{panelTitle}</CardTitle>
            <p className="mt-1 text-sm text-gray-600">
              Record {FRAMEWORK_LABELS[framework].toLowerCase()} by subject each term. This feeds progress tracking,
              cohort analytics, and EHCNA evidence.
            </p>
          </div>
          <Badge variant="outline" className="bg-blue-50 text-blue-800 border-blue-200">
            {yearLabel}
          </Badge>
        </div>
      </CardHeader>
      <CardContent className="space-y-4">
        {yearGroup && (
          <p className="text-sm text-gray-500">
            Year group: <span className="font-medium text-gray-800">{yearGroup}</span>
            {' · '}
            Framework: <span className="font-medium text-gray-800">{FRAMEWORK_LABELS[framework]}</span>
          </p>
        )}

        {legacyBanner && (
          <p className="text-sm text-amber-800 bg-amber-50 border border-amber-200 rounded-md px-3 py-2">
            Legacy overall judgement detected. Values shown for all subjects. Re-save to record subject-level
            attainment.
          </p>
        )}

        <div className="grid gap-4 lg:grid-cols-3">
          {TERM_ORDER.map((term) => (
            <div key={term} className="rounded-lg border border-gray-200 p-4 space-y-3">
              <p className="text-sm font-semibold text-gray-900">{TERM_LABELS[term]}</p>
              <div className="space-y-3">
                {subjects.map((subject) => (
                  <div key={subject} className="space-y-1">
                    <label className="text-xs font-medium text-gray-700">{subject}</label>
                    <select
                      className="w-full rounded-md border border-gray-300 px-2 py-1.5 text-sm disabled:bg-gray-50"
                      value={subjectAttainment[term]?.[subject] || ''}
                      disabled={!canEdit}
                      onChange={(e) => updateLevel(term, subject, e.target.value)}
                    >
                      {descriptorOptions.map((opt) => (
                        <option key={opt.value || 'empty'} value={opt.value}>
                          {opt.label}
                        </option>
                      ))}
                    </select>
                    <input
                      type="text"
                      className="w-full rounded-md border border-gray-200 px-2 py-1 text-xs disabled:bg-gray-50"
                      placeholder="Optional note"
                      value={subjectNotes[term]?.[subject] || ''}
                      disabled={!canEdit}
                      onChange={(e) => updateNote(term, subject, e.target.value)}
                    />
                  </div>
                ))}
              </div>
            </div>
          ))}
        </div>

        {!canEdit && (
          <p className="text-sm text-amber-700 bg-amber-50 border border-amber-200 rounded-md px-3 py-2">
            You can view attainment levels. Class teachers and SENCOs can update them.
          </p>
        )}

        {canEdit && (
          <div className="flex justify-end">
            <button
              type="button"
              onClick={handleSave}
              disabled={saving}
              className="rounded-lg bg-indigo-600 px-4 py-2 text-sm font-medium text-white hover:bg-indigo-700 disabled:opacity-50"
            >
              {saving ? 'Saving…' : 'Save curriculum attainment'}
            </button>
          </div>
        )}
      </CardContent>
    </Card>
  );
}
